import { AxiosResponse } from "axios";
import { MRT_TableInstance } from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { BadRequestError } from "@/api/config/config";
import {
  useSysCfgChangeEnabledMutation,
  useSysCfgCreateMutation,
  useSysCfgDeleteMutation,
  useSysCfgUpdateMutation,
} from "@/api/hooks/sys-cfg-hooks";
import {
  ResultDtoLong,
  SysCfgCreateRequestDto,
  SysCfgRegularDto,
  SysCfgUpdateRequestDto,
} from "@/shared/api";

export const useSaveConfig = ({
  roomName,
  cfgName,
  onSuccessCallback,
}: {
  roomName: string;
  cfgName: string;
  onSuccessCallback?: () => void;
}) => {
  const { t } = useTranslation();
  const { mutate: createCfgMutate, isPending } = useSysCfgCreateMutation();

  const saveConfigMutation = useCallback(
    (config: SysCfgCreateRequestDto) => {
      createCfgMutate(
        {
          domain: roomName,
          cfgName: cfgName,
          body: config,
        },
        {
          onSuccess: () => {
            enqueueSnackbar(t("configAdded"), { variant: "success" });
            onSuccessCallback && onSuccessCallback();
          },
          onError: (err) => {
            if (err instanceof BadRequestError) {
              enqueueSnackbar(`${err.message}`, { variant: "error" });
            } else {
              enqueueSnackbar(
                t("operationFailureContactAdministratorMessage", { message: err.message }),
                {
                  variant: "error",
                }
              );
            }
            console.error("Adding config failure", err);
          },
        }
      );
    },
    [createCfgMutate, roomName, cfgName, onSuccessCallback, t]
  );

  return {
    saveConfigMutation,
    isPending: isPending,
  };
};

export const useUpdateConfig = ({
  roomName,
  cfgName,
  onSuccessCallback,
}: {
  roomName: string;
  cfgName: string;
  onSuccessCallback?: () => void;
}) => {
  const { t } = useTranslation();
  const { mutate: updateCfgMutate, isPending } = useSysCfgUpdateMutation();

  const updateConfigMutation = useCallback(
    (name: string, body: SysCfgUpdateRequestDto) => {
      updateCfgMutate(
        {
          domain: roomName,
          cfgName: cfgName,
          name: name,
          body: body,
        },
        {
          onSuccess: () => {
            enqueueSnackbar(t("configUpdated"), { variant: "success" });
            onSuccessCallback && onSuccessCallback();
          },
          onError: (err) => {
            enqueueSnackbar(
              t("operationFailureContactAdministratorMessage", { message: err.message }),
              {
                variant: "error",
              }
            );
            console.error("Config edit failure", err);
          },
        }
      );
    },
    [cfgName, onSuccessCallback, roomName, updateCfgMutate, t]
  );

  return {
    updateConfigMutation,
    isPending: isPending,
  };
};

export const useConfigChangeEnabled = ({
  roomName,
  cfgName,
  onSuccessCallback,
}: {
  roomName: string;
  cfgName: string;
  onSuccessCallback?: <T extends SysCfgRegularDto>(table: MRT_TableInstance<T>) => void;
}) => {
  const { t } = useTranslation();
  const { mutate: changeEnabledMutate } = useSysCfgChangeEnabledMutation();

  return useCallback(
    <T extends SysCfgRegularDto>(table: MRT_TableInstance<T>, enabled: boolean) => {
      const names = table.getSelectedRowModel().rows.map((item) => item.original.name);

      changeEnabledMutate(
        {
          domain: roomName,
          cfgName: cfgName,
          enabled: enabled,
          filters: new Set([
            { key: "name", mode: "STRICT_IN", value: names as unknown as object },
          ]),
        },
        {
          onSuccess: (response) => {
            enqueueSnackbar(
              t("elementsHasBeenUpdated", {
                data: response.data,
              }),
              {
                variant: "success",
              }
            );
            onSuccessCallback && onSuccessCallback(table);
          },
          onError: (err) => {
            enqueueSnackbar(
              t("operationFailureContactAdministratorMessage", { message: err.message }),
              {
                variant: "error",
              }
            );
            console.error("Change enabled operation failure", err);
          },
        }
      );
    },
    [cfgName, changeEnabledMutate, onSuccessCallback, roomName, t]
  );
};

export const useDeleteConfigs = ({
  roomName,
  cfgName,
  onSuccessCallback,
}: {
  roomName: string;
  cfgName: string;
  onSuccessCallback?: <T extends SysCfgRegularDto>(
    table: MRT_TableInstance<T>,
    response: AxiosResponse<ResultDtoLong, any>
  ) => void;
}) => {
  const { t } = useTranslation();
  const { mutate: deleteCfgsMutate } = useSysCfgDeleteMutation();

  return useCallback(
    <T extends SysCfgRegularDto>(table: MRT_TableInstance<T>) => {
      const names = table.getSelectedRowModel().rows.map((item) => item.original.name);

      deleteCfgsMutate(
        {
          domain: roomName,
          cfgName: cfgName,
          filters: new Set([
            { key: "name", mode: "STRICT_IN", value: names as unknown as object },
          ]),
        },
        {
          onSuccess: (response) => {
            response.data.dataOnError
              ? enqueueSnackbar(t("somethingWentWrong"), { variant: "error" })
              : enqueueSnackbar(
                  t("elementsHasBeenDeletedLength", { data: response.data.data?.toString() }),
                  { variant: "success" }
                );
            onSuccessCallback && onSuccessCallback(table, response);
          },
          onError: (err) => {
            enqueueSnackbar(
              t("operationFailureContactAdministratorMessage", { message: err.message }),
              {
                variant: "error",
              }
            );
            console.error("Delete operation failure", err);
          },
        }
      );
    },
    [cfgName, deleteCfgsMutate, onSuccessCallback, roomName, t]
  );
};
